import React from "react";
import { InsightsOutlined, WorkspacePremiumOutlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { PALETTE as P, CARD, NUM, TYPE, SPACE, tileRow } from "./shared/tokens";
import * as C from "./shared/content";
import {
  Card, CountUp, Delta, Fill, Legend, RankedBarList, AreaChart, LoadingView, stats, areaPath, WipeReveal,
  EmptyState,
} from "./shared/primitives";
import CampaignTable from "../components/CampaignTable";

import WidgetGate from "../components/WidgetGate";
import { ALL_VISIBLE, WIDGET, WIDGET_LABELS } from "../layout/catalog";
import { DragGrid, DragItem, cardGrid, shownCount } from "../dnd/WidgetGridDnd";
import { platformBlocks, PLATFORM_FULL_WIDTH } from "./shared/PlatformBlocks";
// B · Console.
//
// The canvas drew this one on near-black. Backrr has no dark mode, so the
// direction keeps what actually made it a console — density — and drops the
// darkness: tighter rows, smaller type, a lead card that gives the pipeline the
// weight the hero had, and line sparklines rather than columns.
//
// Everything an admin needs is visible without scrolling past a card, which is
// the trade this direction is making against A's roominess.
const LeadCard = ({ children, delay }) => (
  <Box
    className="dv-card"
    style={{ animationDelay: `${delay}s` }}
    sx={{
      ...CARD,
      // The hero's radial wash, translated to the brand's own violet so it
      // reads as emphasis rather than a second theme.
      background: `radial-gradient(120% 120% at 0% 0%, ${P.accentSoft} 0%, ${P.surface} 62%)`,
      display: "flex",
      flexDirection: "column",
      gap: SPACE.md,
    }}
  >
    {children}
  </Box>
);

const LineSpark = ({ values, delay, color }) => {
  const path = areaPath(values, 300, 34);
  return (
    <Box sx={{ position: "relative", height: 34, marginTop: 0.5, display: "flex" }}>
      <WipeReveal delay={delay}>
        {path && (
          <svg viewBox="0 0 300 34" preserveAspectRatio="none" aria-hidden="true" style={{ width: "100%", height: 34, display: "block" }}>
            <path d={path.area} fill={P.accentSoft} />
            <path d={path.line} fill="none" stroke={color} strokeWidth="1.75" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          </svg>
        )}
      </WipeReveal>
    </Box>
  );
};

const DashboardB = ({ data, monetization, platform, platformState = {}, scope, isLoading, view = ALL_VISIBLE, onOrderChange = () => {}, onItemOrderChange = () => {} }) => {
  const shell = {
    background: P.ground,
    borderRadius: "16px",
    // padding: { xs: SPACE.lg, md: SPACE.xl },
    display: "flex",
    flexDirection: "column",
    gap: SPACE.md,
  };

  if (isLoading) {
    return (
      <Box sx={shell}>
        <LoadingView />
      </Box>
    );
  }

  const series = C.seriesFrom(data?.daily);
  const metrics = C.metrics(data, monetization);
  const pipe = C.pipeline(data);
  const plan = C.planMix(monetization);
  const assess = C.assessments(monetization);
  const comp = C.composition(data);
  const camp = C.campaigns(data);

  const planColours = { paid: P.accent, paid_earlier: P.pipeline.interested, free: P.pipeline.invited };
  const stageColour = {
    invited: P.pipeline.invited,
    interested: P.pipeline.interested,
    invested: P.pipeline.invested,
    rejected: P.pipeline.rejected,
  };
  const planTotal = Math.max(plan.total, 1);

    const blocks = {
      key_metrics: (
      <Box component="section" aria-label="Key metrics" sx={tileRow(230, SPACE.md, shownCount(view, "key_metrics", metrics))}>
        <DragGrid {...cardGrid(view, "key_metrics", metrics, onItemOrderChange)}>{view.sortItems("key_metrics", metrics).map((m, i) => (<WidgetGate key={m.key} fill show={view.item("key_metrics", m.key)}><DragItem id={m.key} label={m.label ?? m.title} radius="14px">
          <Box key={m.key} className="dv-card" style={{ animationDelay: `${0.1 + i * 0.065}s` }} sx={{ ...CARD, display: "flex", flexDirection: "column", gap: SPACE.xs }}>
            <Typography sx={{ fontSize: TYPE.label, color: P.inkSoft }}>{m.label}</Typography>
            <Box display="flex" alignItems="baseline" gap={SPACE.sm}>
              <CountUp value={m.value} sx={{ fontSize: "2rem", fontWeight: 600, letterSpacing: "-0.03em", lineHeight: 1.05, color: P.ink }} />
              <Delta value={m.delta} higherIsBetter={m.higherIsBetter} />
            </Box>
            <Typography sx={{ fontSize: TYPE.micro, color: P.inkMuted }}>{m.footnote}</Typography>
            {m.series && <LineSpark values={series[m.series]} delay={0.45 + i * 0.065} color={P.accent} />}
          </Box>
        </DragItem></WidgetGate>))}</DragGrid>
      </Box>
      ),
      pipeline: (
        <LeadCard delay={0.36}>
          <Box>
            <Typography component="h2" sx={{ margin: 0, fontSize: TYPE.section, fontWeight: 600, color: P.ink }}>Pipeline</Typography>
            <Typography sx={{ margin: "2px 0 0", fontSize: TYPE.meta, color: P.inkMuted }}>{pipe.subtitle}</Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
            {pipe.stages.map((s, i) => (
              <Box key={s.key} className="dv-item" style={{ animationDelay: `${0.5 + i * 0.04}s` }}>
                <Box display="flex" justifyContent="space-between" sx={{ fontSize: TYPE.label, marginBottom: "5px" }}>
                  <Typography sx={{ fontSize: TYPE.label, color: P.inkSoft }}>{s.label}</Typography>
                  <Typography sx={{ ...NUM, fontSize: TYPE.label, fontWeight: 600, color: P.ink }}>{s.n}</Typography>
                </Box>
                <Fill ratio={pipe.total ? s.n / pipe.total : 0} color={stageColour[s.key]} delay={0.55 + i * 0.06} height={6} />
              </Box>
            ))}
          </Box>

          <Box sx={{ marginTop: "auto", display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: SPACE.sm, paddingTop: SPACE.md, borderTop: `1px solid ${P.line}` }}>
            {pipe.rates.map((r, i) => (
              <Box key={r.label}>
                <Typography sx={{ display: "block", fontSize: TYPE.micro, color: P.inkMuted }}>{r.label}</Typography>
                <Typography sx={{ ...NUM, fontSize: "1.45rem", fontWeight: 600, letterSpacing: "-0.02em", color: i === 2 ? P.accent : P.ink }}>
                  {r.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </LeadCard>
      ),
      daily_activity: (
        <Card title="Daily Activity" subtitle={C.activity.subtitle} hint="Two panels, one shared date axis, each scaled to its own series. Compare shapes between panels, not heights." delay={0.425} sx={{ gap: SPACE.md }}>
          {C.activity.panels.map((panel, i) => {
            const st = stats(series[panel.key]);
            return (
              <Box key={panel.key} sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Box display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="baseline" gap={SPACE.sm}>
                  <Typography sx={{ display: "inline-flex", alignItems: "center", gap: SPACE.sm, fontSize: TYPE.label, fontWeight: 500, color: P.ink }}>
                    <Box component="span" sx={{ width: 9, height: 9, borderRadius: "3px", background: P.series[i] }} />
                    {panel.name}
                  </Typography>
                  <Typography sx={{ fontSize: TYPE.micro, color: P.inkMuted }}>
                    total <b style={{ ...NUM, color: P.ink }}>{st.total}</b> · peak <b style={{ ...NUM, color: P.ink }}>{st.peak}</b>
                  </Typography>
                </Box>
                <AreaChart values={series[panel.key]} color={P.series[i]} label={`${panel.name}: total ${st.total}, peak ${st.peak}`} delay={0.6 + i * 0.1} height={92} axis={false} gid={`b-${panel.key}`} />
              </Box>
            );
          })}
        </Card>
      ),
      plan_mix: (
        <Card
          title="Plan Mix"
          subtitle={plan.subtitle}
          hint="A startup with no plan is a free (lite) user; one that bought a plan inside the range is paid. Anyone already paying before it is counted separately so the figures still add up to every startup."
          delay={0.49}
          sx={{ gap: SPACE.md }}
          right={<Legend keys={plan.legend.map((l) => ({ label: l.label, color: planColours[l.key] }))} />}
        >
          <Box
            role="img"
            aria-label={plan.legend.map((l) => `${l.label} ${l.value}`).join(", ")}
            sx={{ display: "flex", height: 14, borderRadius: "7px", overflow: "hidden", gap: "2px" }}
          >
            {plan.legend
              .filter((l) => l.value > 0)
              .map((l, i) => (
                <Box key={l.key} sx={{ width: `${(l.value / planTotal) * 100}%`, position: "relative", overflow: "hidden" }}>
                  <Box className="dv-fill" style={{ animationDelay: `${0.7 + i * 0.05}s`, transform: "scaleX(1)" }} sx={{ height: "100%", background: planColours[l.key] }} />
                </Box>
              ))}
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: SPACE.sm }}>
            {plan.legend.map((l) => (
              <Box key={l.key}>
                <Typography sx={{ fontSize: TYPE.micro, color: P.inkMuted }}>{l.label}</Typography>
                <CountUp value={l.value} sx={{ fontSize: "1.2rem", fontWeight: 600, color: P.ink }} />
              </Box>
            ))}
          </Box>
          <Typography sx={{ fontSize: TYPE.micro, color: P.inkMuted }}>{plan.footnote}</Typography>

          <Box sx={{ borderTop: `1px solid ${P.line}`, paddingTop: SPACE.sm, display: "flex", flexDirection: "column", gap: SPACE.xs }}>
            {plan.plans.map((p) => (
              <Box key={p.label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: SPACE.sm, padding: "6px 8px", marginX: "-8px", borderRadius: "8px", "&:hover": { background: P.accentSoft } }}>
                <Typography sx={{ fontSize: TYPE.label, color: P.inkSoft }}>
                  {p.label}
                  {p.show_version && (
                    <Box component="span" sx={{ fontSize: "11px", padding: "0 6px", marginLeft: "6px", borderRadius: "5px", background: P.iconChip, color: P.accent }}>
                      V{p.version}
                    </Box>
                  )}
                </Typography>
                <Typography sx={{ ...NUM, fontSize: TYPE.label, fontWeight: 600, color: P.ink }}>{p.count}</Typography>
              </Box>
            ))}
            {plan.plans.length === 0 && (
              <EmptyState minHeight={96} icon={<WorkspacePremiumOutlined />} title="No active subscriptions yet" text="Plans startups buy will be listed here with how many hold each one." />
            )}
          </Box>
        </Card>
      ),
      assessments: (
        <Card
          title="AI Insights"
          subtitle={assess.subtitle}
          hint="Fundability and pitch-deck reports generated in this range, split by whether the startup unlocked the full result."
          delay={0.555}
          sx={{ gap: SPACE.md }}
          right={<Legend keys={[{ label: "Paid", color: P.accent }, { label: "Free", color: P.pipeline.invited }]} />}
        >
          <DragGrid {...cardGrid(view, "assessments", assess.rows, onItemOrderChange)}>{view.sortItems("assessments", assess.rows).map((r, i) => (<WidgetGate key={r.key} show={view.item("assessments", r.key)}><DragItem id={r.key} label={r.label ?? r.title} radius="14px">
            <Box key={r.key} className="dv-item" style={{ animationDelay: `${0.75 + i * 0.05}s` }} sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              <Box display="flex" justifyContent="space-between">
                <Typography sx={{ fontSize: TYPE.label, color: P.ink }}>{r.label}</Typography>
                <Typography sx={{ ...NUM, fontSize: TYPE.label, fontWeight: 600, color: P.ink }}>{r.total}</Typography>
              </Box>
              <Fill ratio={r.total ? r.paid / r.total : 0} color={P.accent} delay={0.85 + i * 0.05} height={6} />
              <Typography sx={{ fontSize: TYPE.micro, color: P.inkMuted }}>{r.footnote}</Typography>
            </Box>
          </DragItem></WidgetGate>))}</DragGrid>
          {assess.rows.length === 0 && (
            <EmptyState minHeight={96} icon={<InsightsOutlined />} title="No assessments in this range" text="Fundability and pitch-deck reports startups generate in this range will appear here." />
          )}
        </Card>
      ),
      composition: (
      <Box component="section" sx={{ display: "flex", flexDirection: "column", gap: SPACE.md, marginTop: SPACE.xs }}>
        <Box display="flex" flexWrap="wrap" alignItems="baseline" gap={SPACE.sm}>
          <Typography component="h2" sx={{ margin: 0, fontSize: "1rem", fontWeight: 600, color: P.ink }}>Composition</Typography>
          <Typography sx={{ fontSize: TYPE.meta, color: P.inkMuted }}>{comp.summary}</Typography>
        </Box>
        <Box sx={tileRow(280, SPACE.md, shownCount(view, "composition", comp.groups))}>
          <DragGrid {...cardGrid(view, "composition", comp.groups, onItemOrderChange)}>{view.sortItems("composition", comp.groups).map((g, i) => (<WidgetGate key={g.key} fill show={view.item("composition", g.key)}><DragItem id={g.key} label={g.label ?? g.title} radius="14px">
            <Box key={g.key} className="dv-card" style={{ animationDelay: `${0.62 + i * 0.065}s` }} sx={{ ...CARD, display: "flex", flexDirection: "column", gap: SPACE.xs }}>
              <Typography component="h3" sx={{ margin: "0 0 4px", fontSize: TYPE.label, fontWeight: 600, color: P.ink }}>{g.title}</Typography>
              <RankedBarList rows={g.rows} footer={g.footer} delay={0.85 + i * 0.05} expandable={g.expandable} />
            </Box>
          </DragItem></WidgetGate>))}</DragGrid>
        </Box>
      </Box>
      ),
      campaign_attribution: (
      <Box component="section" sx={{ display: "flex", flexDirection: "column", gap: SPACE.sm, marginTop: SPACE.xs }}>
        <Box display="flex" flexWrap="wrap" alignItems="baseline" gap={SPACE.sm}>
          <Typography component="h2" sx={{ margin: 0, fontSize: "1rem", fontWeight: 600, color: P.ink }}>Campaign Attribution</Typography>
          <Typography sx={{ fontSize: TYPE.meta, color: P.inkMuted }}>{camp.summary}</Typography>
        </Box>
        <Box className="dv-card" style={{ animationDelay: ".8s" }}>
          <CampaignTable rows={camp.rows} range={data?.range} />
        </Box>
      </Box>
      ),
      // Startup platform widgets, shared by every variant.
      ...platformBlocks({ platform, state: platformState, view, onItemOrderChange }),
    };
    // Widgets that span the full row in this design; the rest sit two to a row on wide screens.
    const FULL_WIDTH = ["key_metrics", "composition", "campaign_attribution", ...PLATFORM_FULL_WIDTH];
    // The widgets on screen, in order: drag targets, and what pairs up two to a row.
    const shown = view.order.filter((k) => blocks[k] && view.widget(k));

  return (
    <DragGrid ids={shown} order={view.order} labels={WIDGET_LABELS} onOrderChange={onOrderChange}>
    <Box sx={{ ...shell, display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "repeat(2, minmax(0, 1fr))" }, gap: `${view.gap}px`, alignItems: "stretch", gridAutoFlow: "row dense" }}>
      {view.order.filter((k) => blocks[k]).map((k) => (
        <WidgetGate key={k} fill show={view.widget(k)} style={{ gridColumn: view.span(k, FULL_WIDTH, shown), minWidth: 0 }}>
          <DragItem id={k} label={WIDGET[k]?.label ?? k} handle>{blocks[k]}</DragItem>
        </WidgetGate>
      ))}
    </Box>
    </DragGrid>
  );
};

export default DashboardB;
